import { Agency } from "@/types/agencies";
import { InsuranceCarriersEnum } from "@/types/settings";

export type PXEvent =
  | "PX_TAB_CLICKED"
  | "PX_QUOTES_CLICKED"
  | "PX_PROPOSAL_CLICKED"
  | "PX_PROPOSAL_SEND"
  | "PX_SEARCH"
  | "PX_HANDOFF_CLICKED"
  | "PX_QUALIFICATION_CLICKED"
  | "PX_QUALIFICATION_VERIFY_CLICKED"
  | "PX_COMPANY_VIEW"
  | "PX_COMPANY_CLICKED"
  | "AI_CHAT_FEEDBACK"
  | "AI_CHAT_NO_INFORMATION_FOUND"
  | "ASSISTANT_CLICKED"
  | "ASSISTANT_SENT_MESSAGE"
  | "ASSISTANT_RECEIVED_REPLY"
  | "ASSISTANT_CLOSED"
  | "PX_QUOTE_TAB_CLICKED"
  | "PX_INTERNAL_QUOTE_STARTED"
  | "PX_INTERNAL_QUOTE_EDITOR_CLICKED"
  | "PX_INTERNAL_QUOTE_WARNING"
  | "PX_INTERNAL_QUOTE_FINISHED"
  | "PX_INTERNAL_QUOTE_ERROR"
  | "PX_INTERNAL_QUOTE_OPEN"
  | "PX_PROPOSAL_ERROR"
  | "PX_PROPOSAL_MANUAL";

export const eventNames: Record<PXEvent, string> = {
  PX_TAB_CLICKED: "px.tab_clicked",
  PX_QUOTES_CLICKED: "px.quotes_clicked",
  PX_PROPOSAL_CLICKED: "px.proposal_clicked",
  PX_PROPOSAL_SEND: "px.proposal_send",
  PX_SEARCH: "px.search",
  PX_HANDOFF_CLICKED: "px.handoff_clicked",
  PX_QUALIFICATION_CLICKED: "px.qualification_clicked",
  PX_QUALIFICATION_VERIFY_CLICKED: "px.qualification_verify_clicked",
  PX_COMPANY_VIEW: "px.company_view",
  PX_COMPANY_CLICKED: "px.company_clicked",
  AI_CHAT_FEEDBACK: "px.ai_chat_feedback",
  AI_CHAT_NO_INFORMATION_FOUND: "px.ai_chat_no_information_found",
  ASSISTANT_CLICKED: "px.assistant_clicked",
  ASSISTANT_SENT_MESSAGE: "px.assistant_sent.message",
  ASSISTANT_RECEIVED_REPLY: "px.assistant_received.reply",
  ASSISTANT_CLOSED: "px.assistant_closed",
  PX_QUOTE_TAB_CLICKED: "px.quotetab_clicked",
  PX_INTERNAL_QUOTE_STARTED: "px.internalquote_started",
  PX_INTERNAL_QUOTE_EDITOR_CLICKED: "px.internalquote_editor_clicked",
  PX_INTERNAL_QUOTE_WARNING: "px.internalquote_warning",
  PX_INTERNAL_QUOTE_FINISHED: "px.internalquote_finished",
  PX_INTERNAL_QUOTE_ERROR: "px.internalquote_error",
  PX_INTERNAL_QUOTE_OPEN: "px.internalquote_open",
  PX_PROPOSAL_ERROR: "px.proposal_error",
  PX_PROPOSAL_MANUAL: "px.proposal_manual",
};

type PXTabClickedPayload = {
  event: "PX_TAB_CLICKED";
  quoteAgency: Agency;
  quoteUUID?: string;
  tab: "quote_form" | "eligibility" | "proposal" | "quote_results";
  proposalUUID?: string;
  businessUUID?: string;
};

type PXQuotesClickedPayload = {
  event: "PX_QUOTES_CLICKED";
  quoteAgency: Agency;
  quoteUUID: string;
  action: "recovery_link" | "qf_tab" | "copy" | "download";
};

type PXProposalClickedPayload = {
  event: "PX_PROPOSAL_CLICKED";
  quoteAgency: Agency;
  quoteUUID?: string;
  action: "add_rated" | "add_processing" | "proposal_link";
  proposalUUID?: string;
  businessUUID?: string;
};

type PXProposalSendPayload = {
  event: "PX_PROPOSAL_SEND";
  quoteAgency: Agency;
  quoteUUID: string;
  action: "partial" | "final";
};

type PXSearchPayload = {
  event: "PX_SEARCH";
  query: string;
};

type PXHandoffClickedPayload = {
  event: "PX_HANDOFF_CLICKED";
};

type PXQualificationClickedPayload = {
  event: "PX_QUALIFICATION_CLICKED";
};

type PXQualificationVerifyClickedPayload = {
  event: "PX_QUALIFICATION_VERIFY_CLICKED";
  hasDot: boolean;
};

type PXCompanyViewPayload = {
  event: "PX_COMPANY_VIEW";
  businessName: string;
  name: string;
  isEligible: boolean;
  assignedTo: string;
  hasAz: boolean;
  email: string;
};

export type PXCompanyClickedPayload = {
  event: "PX_COMPANY_CLICKED";
  action: "copy" | "clicked";
  component:
    | "crm_link"
    | "phone"
    | "email"
    | "vin"
    | "address"
    | "mc_number"
    | "dot_number"
    | "ein_tax_id"
    | "pgr_link"
    | "geico_link"
    | "motus_link"
    | "verification_link";
};

type AIChatFeedbackPayload = {
  event: "AI_CHAT_FEEDBACK";
  messageId: string;
  messageSent: string;
  messageReceived: string;
  feedback: "positive" | "negative";
};

type AIChatNoInformationFoundPayload = {
  event: "AI_CHAT_NO_INFORMATION_FOUND";
  messageId: string;
  messageSent: string;
  messageReceived: string;
};

type AssistantClickedPayload = {
  event: "ASSISTANT_CLICKED";
  email: string;
  currentUrl: string;
};

type AssistantSentMessagePayload = {
  event: "ASSISTANT_SENT_MESSAGE";
  email: string;
  currentUrl: string;
  messageContent: string;
  threadID: string;
  messageId: string;
  messageReceived: string;
};

type AssistantReceivedReplyPayload = {
  event: "ASSISTANT_RECEIVED_REPLY";
  email: string;
  currentUrl: string;
  replyContent: string;
  threadID: string;
  messageSent: string;
  messageId: string;
};

type AssistantClosedPayload = {
  event: "ASSISTANT_CLOSED";
  email: string;
  currentUrl: string;
};

type PXQuoteTabClickedPayload = {
  event: "PX_QUOTE_TAB_CLICKED";
};

type PXInternalQuoteStartedPayload = {
  event: "PX_INTERNAL_QUOTE_STARTED";
  quote_uuid: string;
};

type PXInternalQuoteEditorClickedPayload = {
  event: "PX_INTERNAL_QUOTE_EDITOR_CLICKED";
  quote_uuid: string;
};

type PXInternalQuoteWarningPayload = {
  event: "PX_INTERNAL_QUOTE_WARNING";
  warning_description: string;
  field_name: string;
  quote_type: string;
  quote_uuid: string;
};

type PXInternalQuoteFinishedPayload = {
  event: "PX_INTERNAL_QUOTE_FINISHED";
  quote_type: string;
  quote_uuid: string;
};

type PXInternalQuoteErrorPayload = {
  event: "PX_INTERNAL_QUOTE_ERROR";
  base_url: string;
  quote_type: string;
  quote_uuid: string;
  field: string;
  description: string;
};

type PXInternalQuoteOpenPayload = {
  event: "PX_INTERNAL_QUOTE_OPEN";
  quote_type: string;
  quote_uuid: string;
};

type PXProposalExtractionFailurePayload = {
  event: "PX_PROPOSAL_ERROR" | "PX_PROPOSAL_MANUAL";
  error_message: string;
  quote_uuid?: string;
  carrier?: InsuranceCarriersEnum;
  file_type?: string;
};

export type PXEventPayload =
  | PXSearchPayload
  | PXProposalSendPayload
  | PXProposalClickedPayload
  | PXQuotesClickedPayload
  | PXTabClickedPayload
  | PXHandoffClickedPayload
  | PXQualificationClickedPayload
  | PXQualificationVerifyClickedPayload
  | PXCompanyViewPayload
  | PXCompanyClickedPayload
  | AIChatFeedbackPayload
  | AIChatNoInformationFoundPayload
  | AssistantClickedPayload
  | AssistantSentMessagePayload
  | AssistantReceivedReplyPayload
  | AssistantClosedPayload
  | PXQuoteTabClickedPayload
  | PXInternalQuoteStartedPayload
  | PXInternalQuoteEditorClickedPayload
  | PXInternalQuoteWarningPayload
  | PXInternalQuoteFinishedPayload
  | PXInternalQuoteErrorPayload
  | PXInternalQuoteOpenPayload
  | PXProposalExtractionFailurePayload;
